/**
 * Erlaubte Dateiformate, Größen und Quoten — die einzige Quelle der Wahrheit
 * für Client und Server (ARCHITECTURE.md §11).
 *
 * Zwei Entscheidungen, die hier festgeschrieben sind:
 *
 * 1. **Eine Allowlist, keine Blocklist.** Was nicht in dieser Tabelle steht,
 *    wird abgelehnt. Eine Blocklist müsste jedes gefährliche Format kennen;
 *    diese Liste muss nur die harmlosen kennen, und die ändern sich selten.
 *
 * 2. **Der Typ kommt aus den ersten Bytes, nicht aus dem Namen und nicht aus
 *    dem `Content-Type`-Header.** Beides ist frei wählbar und damit wertlos.
 *    Der Server prüft die Bytes selbst und schreibt das Ergebnis in die
 *    Datenbank — ein `Content-Type` vom Client wird nie gespeichert.
 *
 * Die Prüfung ist bewusst hier und nicht in einer Bibliothek: Sie ist kurz genug,
 * um sie zu lesen, und eine Abhängigkeit, die 200 Formate erkennt, wäre für eine
 * Allowlist von 14 das falsche Werkzeug.
 */
const MB = 1024 * 1024;
const b = (...bytes) => bytes;
export const FORMATE = [
    // --- Bilder ---
    {
        mime: 'image/jpeg', endungen: ['jpg', 'jpeg'],
        magic: [{ offset: 0, bytes: b(0xff, 0xd8, 0xff) }],
        maxBytes: 25 * MB, vorschau: true, exif: true,
    },
    {
        mime: 'image/png', endungen: ['png'],
        magic: [{ offset: 0, bytes: b(0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a) }],
        maxBytes: 25 * MB, vorschau: true, exif: true,
    },
    {
        mime: 'image/webp', endungen: ['webp'],
        // RIFF????WEBP — die vier Bytes dazwischen sind die Länge.
        magic: [{
                offset: 0,
                bytes: b(0x52, 0x49, 0x46, 0x46, null, null, null, null, 0x57, 0x45, 0x42, 0x50),
            }],
        maxBytes: 25 * MB, vorschau: true, exif: true,
    },
    {
        mime: 'image/gif', endungen: ['gif'],
        magic: [
            { offset: 0, bytes: b(0x47, 0x49, 0x46, 0x38, 0x37, 0x61) },
            { offset: 0, bytes: b(0x47, 0x49, 0x46, 0x38, 0x39, 0x61) },
        ],
        maxBytes: 15 * MB, vorschau: true, exif: false,
    },
    {
        mime: 'image/heic', endungen: ['heic', 'heif'],
        // ftyp-Box ab Byte 4, Marke `heic`/`heix`/`mif1`.
        magic: [
            { offset: 4, bytes: b(0x66, 0x74, 0x79, 0x70, 0x68, 0x65, 0x69, 0x63) },
            { offset: 4, bytes: b(0x66, 0x74, 0x79, 0x70, 0x68, 0x65, 0x69, 0x78) },
            { offset: 4, bytes: b(0x66, 0x74, 0x79, 0x70, 0x6d, 0x69, 0x66, 0x31) },
        ],
        maxBytes: 30 * MB, vorschau: true, exif: true,
    },
    // --- Video und Ton ---
    {
        mime: 'video/mp4', endungen: ['mp4', 'm4v'],
        magic: [
            { offset: 4, bytes: b(0x66, 0x74, 0x79, 0x70, 0x69, 0x73, 0x6f, 0x6d) },
            { offset: 4, bytes: b(0x66, 0x74, 0x79, 0x70, 0x6d, 0x70, 0x34) },
            { offset: 4, bytes: b(0x66, 0x74, 0x79, 0x70, 0x4d, 0x53, 0x4e, 0x56) },
            { offset: 4, bytes: b(0x66, 0x74, 0x79, 0x70, 0x61, 0x76, 0x63, 0x31) },
        ],
        maxBytes: 500 * MB, vorschau: false, exif: true,
    },
    {
        mime: 'video/quicktime', endungen: ['mov'],
        magic: [{ offset: 4, bytes: b(0x66, 0x74, 0x79, 0x70, 0x71, 0x74, 0x20, 0x20) }],
        maxBytes: 500 * MB, vorschau: false, exif: true,
    },
    {
        mime: 'video/webm', endungen: ['webm'],
        magic: [{ offset: 0, bytes: b(0x1a, 0x45, 0xdf, 0xa3) }],
        maxBytes: 500 * MB, vorschau: false, exif: false,
    },
    {
        mime: 'audio/mpeg', endungen: ['mp3'],
        magic: [
            { offset: 0, bytes: b(0x49, 0x44, 0x33) }, // ID3
            { offset: 0, bytes: b(0xff, 0xfb) }, // MPEG-1 Layer 3
            { offset: 0, bytes: b(0xff, 0xf3) },
            { offset: 0, bytes: b(0xff, 0xf2) },
        ],
        maxBytes: 100 * MB, vorschau: false, exif: false,
    },
    {
        mime: 'audio/ogg', endungen: ['ogg', 'oga', 'opus'],
        magic: [{ offset: 0, bytes: b(0x4f, 0x67, 0x67, 0x53) }],
        maxBytes: 100 * MB, vorschau: false, exif: false,
    },
    // --- Dokumente ---
    {
        mime: 'application/pdf', endungen: ['pdf'],
        magic: [{ offset: 0, bytes: b(0x25, 0x50, 0x44, 0x46, 0x2d) }],
        maxBytes: 50 * MB, vorschau: false, exif: false,
    },
    {
        // Deckt docx/xlsx/pptx und odt mit ab — alle sind ZIP-Container. Eine feinere
        // Unterscheidung bräuchte das Auspacken des Archivs; das findet auf dem
        // Server nicht statt (Zip-Bomben, SSRF über eingebettete Referenzen).
        mime: 'application/zip', endungen: ['zip', 'docx', 'xlsx', 'pptx', 'odt', 'ods'],
        magic: [
            { offset: 0, bytes: b(0x50, 0x4b, 0x03, 0x04) },
            { offset: 0, bytes: b(0x50, 0x4b, 0x05, 0x06) }, // leeres Archiv
        ],
        maxBytes: 100 * MB, vorschau: false, exif: false,
    },
    {
        mime: 'text/plain', endungen: ['txt', 'md', 'csv'],
        // Text hat kein Magic-Byte. Erkannt wird er über die Abwesenheit von
        // Steuerzeichen — siehe `erkenneFormat()`.
        magic: [],
        maxBytes: 5 * MB, vorschau: false, exif: false,
    },
];
/**
 * Formate, die absichtlich FEHLEN. Steht als ausführbare Dokumentation hier,
 * damit die Begründung nicht verlorengeht, wenn jemand sie vermisst.
 */
export const BEWUSST_ABGELEHNT = {
    'image/svg+xml': 'SVG ist ausführbares XML mit <script> und externen Referenzen. Auch von der ' +
        'Blob-Origin aus wäre es ein unnötiges Risiko — und niemand verschickt in einer ' +
        'Familie SVGs.',
    'text/html': 'Dasselbe in laut. Eine hochgeladene HTML-Datei ist eine Webseite auf unserem Server.',
    'application/x-msdownload': 'Ausführbare Dateien haben in einem Familien-Speicher nichts zu suchen.',
    'application/xml': 'XXE-Angriffsfläche, kein erkennbarer Nutzen für diese Zielgruppe.',
};
/** Bytes, die am Anfang gelesen werden müssen, damit jedes Muster prüfbar ist. */
export const SNIFF_BYTES = 32;
/**
 * Erkennt das Format an den ersten Bytes.
 *
 * `null` heißt: nicht in der Allowlist → Ablehnung. Es gibt bewusst keinen
 * „unbekannt, aber vielleicht harmlos"-Fall.
 */
export function erkenneFormat(kopf) {
    for (const format of FORMATE) {
        for (const { offset, bytes } of format.magic) {
            if (passt(kopf, offset, bytes))
                return format;
        }
    }
    // Text zuletzt: Er hat kein Muster, also würde er sonst alles schlucken.
    return istText(kopf) ? (FORMATE.find((f) => f.mime === 'text/plain') ?? null) : null;
}
function passt(kopf, offset, muster) {
    if (kopf.length < offset + muster.length)
        return false;
    for (let i = 0; i < muster.length; i++) {
        const erwartet = muster[i];
        if (erwartet !== null && kopf[offset + i] !== erwartet)
            return false;
    }
    return true;
}
/**
 * Text ist alles, was keine Steuerzeichen außer Tab/CR/LF enthält und gültiges
 * UTF-8 ist. Eine leere Datei gilt nicht als Text — sie hat kein Format.
 */
function istText(kopf) {
    if (kopf.length === 0)
        return false;
    for (const byte of kopf) {
        if (byte === 0)
            return false;
        if (byte < 0x20 && byte !== 0x09 && byte !== 0x0a && byte !== 0x0d)
            return false;
    }
    try {
        new TextDecoder('utf-8', { fatal: true }).decode(kopf);
        return true;
    }
    catch {
        // Der Kopf kann mitten in einem Mehrbyte-Zeichen enden — das ist kein Fehler.
        return kopf.length >= SNIFF_BYTES;
    }
}
export function formatFuer(mime) {
    return FORMATE.find((f) => f.mime === mime) ?? null;
}
/**
 * Größe eines Upload-Blocks: 1 MiB.
 *
 * Der Wert ist ein Kompromiss für schlechte Verbindungen (Anf. 76): Bei
 * 3G-Geschwindigkeit (~400 kbit/s) dauert ein Block rund 20 Sekunden — kurz
 * genug, dass ein Abbruch wenig Arbeit vernichtet, lang genug, dass der
 * Overhead pro Block nicht ins Gewicht fällt. Bricht die Verbindung, wird nur
 * der angefangene Block wiederholt, nicht die Datei.
 */
export const CHUNK_BYTES = 1024 * 1024;
/** Wie lange ein angefangener Upload aufgehoben wird, bevor er verfällt. */
export const UPLOAD_TTL_STUNDEN = 24;
/** Lebensdauer einer signierten Download-URL. */
export const URL_TTL_SEKUNDEN = 300;
/** Speicherplatz je Person, wenn nichts anderes gesetzt ist. */
export const QUOTE_PERSON_BYTES = 10 * 1024 * MB; // 10 GiB
/** Absolute Obergrenze für die ganze Familie. */
export const QUOTE_FAMILIE_BYTES = 50 * 1024 * MB; // 50 GiB
/** Größtes überhaupt erlaubtes Vorschaubild — verhindert Vorschau-als-Ablage. */
export const THUMB_MAX_BYTES = 200 * 1024;
/** Kantenlänge des Vorschaubilds. */
export const THUMB_KANTE = 320;
/**
 * Was der Nutzer über den Virenscan erfährt.
 *
 * `skipped` ist der ehrliche Zustand für Zone A: Der Server hat dort keinen
 * Klartext und kann deshalb prinzipiell nicht scannen. Das als „sauber"
 * anzuzeigen wäre eine Lüge (ARCHITECTURE.md §11).
 */
export const SCAN_TEXT = {
    pending: 'Wird geprüft…',
    clean: 'Auf Viren geprüft',
    infected: 'Schadsoftware gefunden — Datei gesperrt',
    skipped: 'Nicht geprüft: verschlüsselt hochgeladen, der Server kann sie nicht lesen',
    unavailable: 'Nicht geprüft: der Virenscanner läuft gerade nicht',
};
/**
 * Wie lange ein Foto online bleibt — in der Galerie und im Chat.
 *
 * Dans Vorgabe (2026-10-10): Fotos laufen nach sieben Tagen ab und werden
 * dann endgültig gelöscht. Wer sie behalten will, speichert sie auf dem
 * eigenen Gerät. Die Frist zählt ab dem Hochladen; unter jedem Foto steht,
 * wie lange es noch da ist. Der Server schreibt sie als `expires_at` in die
 * Zeile (Migration 0025) — diese Konstante ist die einzige Stelle, an der die
 * Zahl steht, auch für die Tests.
 */
export const FOTO_LEBENSDAUER_TAGE = 7;
//# sourceMappingURL=media.js.map